import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Beaker, Leaf, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { References } from "@/components/References";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import heroAsset from "@/assets/blog/berberine-metformin.jpg.asset.json";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";

const URL = "https://truwe.in/blog/berberine-vs-metformin-india";
const TITLE = "Berberine vs Metformin: What the Trials Actually Show (India, 2026) | TruWe";

export const Route = createFileRoute("/blog/berberine-vs-metformin-india")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: "berberine-vs-metformin-india",
      title: TITLE,
      description:
        "Berberine vs metformin, a head-to-head of the human trials on insulin, lipids, PCOS and fatty liver. When berberine is enough, when metformin wins, and how they combine.",
      ogTitle: "Berberine vs Metformin. What the Trials Show",
      ogDescription:
        "Both activate AMPK. But the trials tell a specific story about who each one is best for. Here's the head-to-head.",
      headline: "Berberine vs Metformin: What the Trials Actually Show",
      breadcrumbName: "Berberine vs Metformin",
      datePublished: "2026-07-24",
      dateModified: "2026-07-25",
      ogImage: `https://truwe.in${heroAsset.url}`,
      faqs: [
        {
          q: "Can berberine replace metformin?",
          a: "For pre-diabetes, PCOS-related insulin resistance and mild NAFLD, several head-to-head RCTs show comparable results. In diagnosed type 2 diabetes with high HbA1c, metformin is still first-line, don't stop it without your doctor.",
        },
        {
          q: "Can berberine and metformin be taken together?",
          a: "Yes, some clinicians layer a lower dose of each to reduce GI side-effects of metformin. Always coordinate with your doctor to monitor blood sugar.",
        },
        {
          q: "What are berberine side-effects?",
          a: "Mainly GI, mild bloating, softer stools, occasional constipation. Split into 2-3 doses with meals and reduce if needed. Avoid in pregnancy.",
        },
        {
          q: "How long until berberine shows results?",
          a: "Fasting glucose and insulin improve in 4-8 weeks. Lipid and HbA1c changes are typically clear at 3 months.",
        },
      ],
    }),
});

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Metabolic Health
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            Berberine vs metformin: what the trials actually show
          </h1>
          <BlogByline date="2026-07-24" readingTime="10 min read" />
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Both berberine and metformin activate AMPK, the master metabolic switch
            that tells cells to burn fuel instead of storing it. That's the shared
            mechanism. The evidence is more specific than the internet says. Here's the
            head-to-head, without the hype.
          </p>
        <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={heroAsset.url}
              alt="Berberine vs metformin, clinical comparison for metabolic health"
              width={1200}
              height={630}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            What each one is (in one line)
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Metformin</strong> a 60-year-old, well-studied prescription
              drug. First-line for type 2 diabetes. Cheap, effective, safe long-term.
            </li>
            <li>
              <strong>Berberine</strong> an alkaloid from plants like <em>Berberis
              aristata</em> (Indian barberry / daruharidra). Not a drug. Multiple modern
              RCTs, mostly for insulin resistance, dyslipidaemia and PCOS.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Head-to-head, what the RCTs show
          </h2>
          <div className="mt-5 space-y-4">
            {[
              {
                name: "Fasting glucose & HbA1c (type 2 diabetes)",
                note: "Berberine 500 mg 3× / day and metformin 500 mg 3× / day produced comparable HbA1c drops in a landmark Yin et al. trial. Metformin is still first-line at diagnosis; berberine is an option for pre-diabetes or as an adjunct.",
              },
              {
                name: "Fasting insulin & HOMA-IR",
                note: "Berberine consistently lowers fasting insulin and HOMA-IR, often as effectively as metformin. Both are excellent choices in insulin-resistant PCOS.",
              },
              {
                name: "Lipids (triglycerides, LDL, total cholesterol)",
                note: "Berberine wins here. Multiple trials show larger drops in triglycerides and LDL than metformin. Berberine's PCSK9 inhibition is the mechanism.",
              },
              {
                name: "PCOS-specific outcomes",
                note: "In head-to-head PCOS trials, berberine matched or slightly beat metformin on waist circumference, testosterone and cycle regularity, with less GI upset.",
              },
              {
                name: "NAFLD / fatty liver",
                note: "Berberine reduced hepatic fat and ALT/AST in multiple trials, sometimes on par with lifestyle-plus-metformin. Promising as an adjunct.",
              },
              {
                name: "Side-effect profile",
                note: "Metformin: nausea, diarrhoea (usually resolves in weeks), B12 depletion over years. Berberine: mainly mild GI. Neither causes hypoglycaemia on its own.",
              },
            ].map((row) => (
              <div key={row.name} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-lg font-semibold text-brand-deep">{row.name}</h3>
                </div>
                <p className="mt-2 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            When each one is the right pick
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Diagnosed type 2 diabetes with HbA1c &gt; 7:</strong> Metformin
              first, with lifestyle. Add or discuss berberine only with your doctor.
            </li>
            <li>
              <strong>Pre-diabetes, insulin-resistant PCOS, high triglycerides:</strong>{" "}
              Berberine is a strong evidence-based option. Some clinicians layer a
              lower dose of metformin if needed.
            </li>
            <li>
              <strong>Fatty liver + insulin resistance:</strong> Berberine plus the
              lifestyle stack (see the fatty liver diet guide) has good early data.
            </li>
            <li>
              <strong>Trying to conceive:</strong> Metformin has better safety data
              in pregnancy; berberine should be stopped before conception. Discuss
              with your gynaecologist.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Dose & timing (adults)
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Berberine HCl 500 mg, 2-3 times daily with meals.</strong> Bio-
              availability is modest; splitting the dose is what makes it work.
            </li>
            <li>
              <strong>Avoid the "dihydroberberine" and "phytosome" hype</strong> if the
              underlying dose isn't studied. Standard berberine HCl at trial doses is
              what the evidence supports.
            </li>
            <li>
              <strong>Don't combine with high-dose grapefruit or CYP3A4 inhibitors</strong>
              without medical advice, berberine can raise blood levels of some drugs.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14 rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              <Sparkles className="h-4 w-4" /> The TruWe formulation
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              Ova Balance, berberine paired with inositol, in one sachet
            </h2>
            <p className="mt-3 text-base text-foreground/85">
              Ova Balance combines <strong>berberine HCl with 40:1 myo:D-chiro-inositol
              and Quatrefolic® L-5-MTHF</strong> the exact combination that hits
              insulin resistance from two angles. If PCOS or pre-diabetic insulin
              resistance is your concern, this is the research-backed stack.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                { icon: Leaf, label: "Berberine HCl" },
                { icon: ShieldCheck, label: "Third-party tested" },
                { icon: Beaker, label: "Research-backed doses" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 rounded-xl bg-background/70 px-3 py-3 text-sm text-foreground/85 ring-1 ring-border"
                >
                  <Icon className="h-4 w-4 text-brand" />
                  {label}
                </div>
              ))}
            </div>
            <Link
              to="/products/$handle"
              params={{ handle: "ova-balance" }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-luxury transition hover:bg-brand-deep"
            >
              Explore Ova Balance <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="hormonal" />
          <ClusterLinks cluster="hormonal" />
          <AuthorityFlow cluster="hormonal" />
          <References slug="berberine-vs-metformin-india" />
          <RelatedReading articles={relatedArticles("berberine-vs-metformin-india")} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            Educational only. Do not stop metformin without your doctor. Do not start
            berberine in pregnancy, while breastfeeding, or if you take CYP3A4-metabolised
            medications, without medical advice.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
